import type {
  Agent,
  Config,
  FileDiff,
  LspStatus,
  McpStatus,
  Message,
  Part,
  Path,
  PermissionRequest,
  Project,
  QuestionRequest,
  Session,
  SessionStatus,
  VcsInfo,
} from "@mux/sdk/v2/client"
import type { Accessor } from "solid-js"
import type { SetStoreFunction, Store } from "solid-js/store"
import type { ControlProviderStore } from "@/context/provider-types"
import type { QueuedFollowup } from "@/context/session/followups"
import type { SlashCommandInfo } from "@/transport/directory-bootstrap"
import type { MachineWorkspace, RuntimePath } from "@/transport/runtime-bootstrap"
import type { RequestRoute } from "@/transport/requests"
import type { InitError } from "../../pages/error"

export type ProjectMeta = {
  name?: string
  icon?: {
    override?: string
    color?: string
  }
  commands?: {
    start?: string
  }
}

export type ProviderInventory = ControlProviderStore

export type State = {
  status: "loading" | "partial" | "complete"
  agent: Agent[]
  command: SlashCommandInfo[]
  project: string
  projectMeta: ProjectMeta | undefined
  icon: string | undefined
  provider: ProviderInventory
  config: Config
  path: Path
  mcp: {
    [name: string]: McpStatus
  }
  lsp: LspStatus[]
  vcs: VcsInfo | undefined
  limit: number
}

export type SessionDiffSummary = {
  revision: number
  files: number
  additions: number
  deletions: number
}

export type PtyActivity = {
  alive?: boolean
  active?: boolean
  ptyID?: string
  updatedAt?: number
}

/** The machine's working chats and every workspace's Oracle, with run statuses, from `GET /session/sidebar`. */
export type SidebarProjection = {
  sessions: Session[]
  statuses: Record<string, SessionStatus>
}

/**
 * What this page's sidebar tray last drew from the machine: the projection's
 * chats and the chats open requests surface in, their run state, and the
 * open requests themselves.
 */
export type SidebarProjectionCache = SidebarProjection & {
  /** When the page last wrote it; older run state and requests are not drawn. */
  capturedAt: number
  waiting: Record<string, number>
  requests: Pick<GlobalStore, "question" | "permission" | "request_route">
}

export type GlobalBootstrapCache = Pick<GlobalStore, "path" | "project" | "provider" | "config" | "machineWorkspace"> & {
  updatedAt: number
}

export type GlobalStore = {
  ready: boolean
  error?: InitError
  path: RuntimePath
  project: Project[]
  /** The workspace whose Oracle is the Mux Oracle; absent on runtimes without one. */
  machineWorkspace?: MachineWorkspace
  provider: ProviderInventory
  config: Config
  
  // Global Session and Message Data
  session: Session[]
  /**
   * By chat: the revision at which the store removed it (deleted, trashed, or
   * found missing), so an older copy from any path cannot bring it back.
   */
  session_removed: Record<string, number | undefined>
  sessionTotal: Record<string, number | undefined>
  session_status: Record<string, SessionStatus | undefined>
  /**
   * How many results a chat is still owed: launches that have not reported
   * back and commands still running in the background. The chat's work is
   * unfinished while any is, even with no run in progress.
   */
  session_waiting: Record<string, number | undefined>
  /** TUI/PTY activity — separate from chat session_status. */
  pty_activity: Record<string, PtyActivity | undefined>
  session_diff: Record<string, FileDiff[] | undefined>
  session_diff_summary: Record<string, SessionDiffSummary | undefined>
  session_followup: Record<string, QueuedFollowup[] | undefined>
  permission: Record<string, PermissionRequest[] | undefined>
  question: Record<string, QuestionRequest[] | undefined>
  /** By asking chat: the chats its open requests surface in. */
  request_route: Record<string, RequestRoute | undefined>
  message: Record<string, Message[] | undefined>
  message_actor: Record<string, string | undefined>
  part: Record<string, Part[] | undefined>
}

export type VcsCache = {
  store: Store<{ value: VcsInfo | undefined }>
  setStore: SetStoreFunction<{ value: VcsInfo | undefined }>
  ready: Accessor<boolean>
}

export type MetaCache = {
  store: Store<{ value: ProjectMeta | undefined }>
  setStore: SetStoreFunction<{ value: ProjectMeta | undefined }>
  ready: Accessor<boolean>
}

export type IconCache = {
  store: Store<{ value: string | undefined }>
  setStore: SetStoreFunction<{ value: string | undefined }>
  ready: Accessor<boolean>
}

export type ChildOptions = {
  bootstrap?: boolean
}

export type DirState = {
  lastAccessAt: number
}

export type EvictPlan = {
  stores: string[]
  state: Map<string, DirState>
  pins: Set<string>
  max: number
  ttl: number
  now: number
}

export type DisposeCheck = {
  directory: string
  hasStore: boolean
  pinned: boolean
  booting: boolean
  loadingSessions: boolean
}

export const MAX_DIR_STORES = 30
export const DIR_IDLE_TTL_MS = 20 * 60 * 1000
export const ROOT_SESSION_PAGE_LIMIT = 10
export const SESSION_RECENT_WINDOW = 4 * 60 * 60 * 1000
export const SESSION_RECENT_LIMIT = 50
